import { useState, useEffect, memo, useRef } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Clock, Calendar, ArrowRightLeft, AlertTriangle, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

/**
 * GameweekBanner — sits above the Stage tab. Shows:
 *   - current gameweek + deadline countdown
 *   - "Transfers Open" pill, augmented with a usage/cost preview when
 *     the user has a finalized team (so they know at-a-glance what
 *     their NEXT transfer will cost in real ₣).
 *
 * Transfer penalty rate matches the backend formula
 * (routes/teams.py → /teams/transfer):
 *
 *   used=0 → 0 %         "FREE"
 *   used=1 → 2 %
 *   used≥2 → 5 % + (used-2) × 2.5 %    capped at 90 %
 *
 * v5.7.49 — pre-start league mode. When the user's league has
 * `league_status === "pre_start"`, the banner morphs into a
 * draft-window countdown ("Draft Window — locks in 2d 14h") and
 * fires browser notifications + sticky toasts at 24h-out and
 * 3h-out (only once per deadline; tracked in localStorage so a
 * page-refresh doesn't re-notify). Helps users who otherwise
 * would miss the start of scoring because their team isn't
 * finalized when the league flips to "active".
 */

function nextPenaltyRate(used) {
  if (used <= 0) return 0;
  if (used === 1) return 0.02;
  return Math.min(0.90, 0.05 + (used - 2) * 0.025);
}

// Notification thresholds (hours before the lock).
const DRAFT_NOTIFY_THRESHOLDS = [
  { key: '24h', hours: 24, label: '24 hours' },
  { key: '3h',  hours: 3,  label: '3 hours'  },
];

function _localStorageKey(leagueId, thresholdKey) {
  return `draft_notify_${leagueId}_${thresholdKey}`;
}

function _tryBrowserNotify(title, body, tag) {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'granted') return false;
  try {
    new Notification(title, { body, tag, icon: '/favicon.ico' });
    return true;
  } catch { return false; }
}

export default memo(function GameweekBanner({ team, rosterFull }) {
  const [gw, setGw] = useState(null);
  const _notifyRequested = useRef(false);

  useEffect(() => {
    // v5.7.48 — pass team.league_id so the response uses the league's
    // own start_date+weeks counter ("Game Week 4 of 13") instead of the
    // legacy global season.
    const params = team?.league_id ? { league_id: team.league_id } : {};
    api.get('/gameweek/current', { params }).then(res => setGw(res.data)).catch(() => {});
  }, [team?.league_id]);

  // Draft-deadline notifier: when the user's league is pre-start AND
  // their team isn't finalized AND we cross a 24h/3h threshold, emit
  // both a sonner toast and (if granted) a browser notification.
  useEffect(() => {
    if (!gw || gw.league_status !== 'pre_start') return;
    if (!team?.league_id) return;
    if (team?.finalized) return;  // already done — nothing to remind about
    const start = gw.scoring_starts_at ? new Date(gw.scoring_starts_at) : null;
    if (!start) return;
    const hoursLeft = (start - Date.now()) / 3_600_000;
    if (hoursLeft <= 0) return;

    // Request permission once per session (silent fallback if denied).
    if (typeof Notification !== 'undefined' && Notification.permission === 'default' && !_notifyRequested.current) {
      _notifyRequested.current = true;
      Notification.requestPermission().catch(() => {});
    }

    for (const t of DRAFT_NOTIFY_THRESHOLDS) {
      if (hoursLeft > t.hours) continue;
      const lsKey = _localStorageKey(team.league_id, t.key);
      if (typeof localStorage === 'undefined') break;
      if (localStorage.getItem(lsKey)) continue;  // already notified
      const title = `Draft locks in ${t.label}`;
      const body = `Your team in this league isn't finalized yet. Scoring starts at ${start.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}.`;
      toast.warning(title, {
        description: body,
        duration: 10000,
        action: { label: 'Open Stage', onClick: () => { window.location.hash = '#stage'; } },
      });
      _tryBrowserNotify(title, body, lsKey);
      try { localStorage.setItem(lsKey, String(Date.now())); } catch { /* private mode */ }
      break;  // only fire one toast per render — let user see it before piling 3h on top of 24h
    }
  }, [gw, team?.league_id, team?.finalized]);

  if (!gw) return null;

  const isPreStart = gw.league_status === 'pre_start';
  // 2026-10-12 — 1+ picks is enough to go live (open slots fill fee-free later).
  const picked = (team?.slots || []).length;

  // ── PRE-START MODE: draft-window countdown ────────────────────────
  if (isPreStart && gw.scoring_starts_at) {
    const lockAt = new Date(gw.scoring_starts_at);
    const msLeft = Math.max(0, lockAt.getTime() - Date.now());
    const hoursLeft = msLeft / 3_600_000;
    const daysLeft = Math.floor(hoursLeft / 24);
    const remHours = Math.floor(hoursLeft - daysLeft * 24);
    const remMins = Math.floor((msLeft / 60_000) - daysLeft * 24 * 60 - remHours * 60);
    const timeStr = daysLeft > 0
      ? `${daysLeft}d ${remHours}h`
      : (remHours > 0 ? `${remHours}h ${remMins}m` : `${remMins}m`);
    const isUrgent = hoursLeft <= 24;
    const isCritical = hoursLeft <= 3;
    return (
      <div
        className={`border rounded-xl px-4 py-3 ${
          isCritical ? 'bg-red-500/10 border-red-500/40' :
          isUrgent ? 'bg-amber-500/10 border-amber-500/40' :
          'bg-card border-border/60'
        }`}
        data-testid="gameweek-banner"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className={`h-4 w-4 shrink-0 ${
              isCritical ? 'text-red-500' : isUrgent ? 'text-amber-500' : 'text-[hsl(var(--accent))]'
            }`} />
            <span className="text-sm font-semibold truncate" data-testid="gameweek-label">
              Draft Window — locks in {timeStr}
            </span>
          </div>
          <Badge variant="secondary" className="text-[10px] tabular-nums shrink-0">
            <Clock className="h-2.5 w-2.5 mr-0.5" /> {lockAt.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Badge>
        </div>
        {team && !team.finalized && (
          rosterFull ? (
            <p className="text-[11px] font-semibold text-[hsl(var(--accent))] mt-1.5" data-testid="finalize-nudge-banner">
              🏆 Your roster is complete! One tap left — hit Finalize below so every event scores for you from lock-time.
            </p>
          ) : picked > 0 ? (
            <p className="text-[11px] font-semibold text-[hsl(var(--accent))] mt-1.5" data-testid="finalize-nudge-banner">
              🏆 You can go live now with your {picked} pick{picked === 1 ? '' : 's'} — tap Finalize on Stage; the open slots fill later, free of any fee.
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="draft-not-finalized-hint">
              ⚠️ Your team isn't finalized — any events that happen between the lock and your finalize moment will NOT score for you. Draft one person and tap Finalize on Stage.
            </p>
          )
        )}
        {team?.finalized && (
          <p className="text-[11px] text-green-700 dark:text-green-400 mt-1.5">
            ✓ Your team is finalized — you'll get every event from lock-time forward.
          </p>
        )}
      </div>
    );
  }

  // ── ACTIVE MODE (Week X of Y countdown to week-end) ───────────────
  const deadline = new Date(gw.deadline);
  const now = new Date();
  const hoursLeft = Math.max(0, Math.floor((deadline - now) / (1000 * 60 * 60)));
  const daysLeft = Math.floor(hoursLeft / 24);
  const timeLabel = daysLeft > 0 ? `${daysLeft}d ${hoursLeft % 24}h` : `${hoursLeft}h`;
  const progress = Math.max(0, Math.min(100, (1 - hoursLeft / (7 * 24)) * 100));

  const weekLabel = gw.total_weeks
    ? `Gameweek ${gw.gameweek} of ${gw.total_weeks}`
    : `Gameweek ${gw.gameweek}`;

  // Transfer counter — only meaningful once the team is finalized.
  const showCounter = team?.finalized && gw.is_transfer_window_open;
  const used = team?.weekly_transfers_used || 0;
  const freeFills = team?.finalized ? Math.max(0, Number(team.free_fills_remaining) || 0) : 0;
  const rate = freeFills > 0 ? 0 : nextPenaltyRate(used);
  const budget = team?.budget_remaining || 0;
  const nextCost = rate * budget;

  return (
    <div className="bg-card border border-border/60 rounded-xl px-4 py-3" data-testid="gameweek-banner">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[hsl(var(--accent))]" />
          <span className="text-sm font-semibold" data-testid="gameweek-label">{weekLabel}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {gw.is_transfer_window_open && (
            <Badge className="bg-green-500/15 text-green-700 dark:text-green-300 text-[9px]">
              <ArrowRightLeft className="h-2.5 w-2.5 mr-0.5" /> Transfers Open
            </Badge>
          )}
          <Badge variant="secondary" className="text-[10px] tabular-nums">
            <Clock className="h-2.5 w-2.5 mr-0.5" /> {timeLabel} left
          </Badge>
        </div>
      </div>

      {showCounter && (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2 text-[10px] text-muted-foreground"
          data-testid="gameweek-transfer-counter"
        >
          <span>
            Roster moves used this week: <b className="text-foreground tabular-nums">{used}</b>
          </span>
          <span className="opacity-50">·</span>
          {freeFills > 0 ? (
            <span className="text-green-700 dark:text-green-400 font-medium" data-testid="gameweek-free-fills">
              Next add is FREE — {freeFills} open-slot fill{freeFills === 1 ? '' : 's'} left
            </span>
          ) : used === 0 ? (
            <span className="text-green-700 dark:text-green-400 font-medium">
              Next move (Transfer or Remove) is FREE
            </span>
          ) : (
            <span>
              Next move costs{' '}
              <b className="text-foreground tabular-nums">
                {(rate * 100).toFixed(1)}%
              </b>{' '}
              of your remaining budget (≈
              <b className="text-foreground tabular-nums">
                {' '}{formatFranc(nextCost)}
              </b>
              )
            </span>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
                aria-label="How roster move fees work"
                data-testid="penalty-info-btn"
              >
                <Info className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 text-xs" data-testid="penalty-info-popover">
              <p className="font-semibold text-sm mb-1.5">Roster Move Fees</p>
              <p className="text-muted-foreground mb-2 leading-relaxed">
                <b className="text-foreground">Adds count as moves — drops are free.</b> Every add after
                your first each week charges a fee — a percentage of your <b className="text-foreground">remaining
                budget</b> (cash), on top of the player's price. Dropping someone refunds their
                current market price with no fee. The counter resets every gameweek.
              </p>
              <div className="space-y-0.5 mb-2">
                {[['1st move', 'FREE'], ['2nd move', '2%'], ['3rd move', '5%'], ['4th move', '7.5%'], ['5th move', '10%']].map(([mv, fee]) => (
                  <div key={mv} className="flex justify-between border-b border-border/40 py-0.5">
                    <span>{mv}</span>
                    <b className={`tabular-nums ${fee === 'FREE' ? 'text-green-700 dark:text-green-400' : ''}`}>{fee}</b>
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground italic">…then +2.5% per extra move, capped at 90%</p>
              {used > 0 && (
                <p className="mt-2 rounded-lg bg-[hsl(var(--accent)/0.08)] p-2" data-testid="penalty-info-your-status">
                  You've made <b>{used}</b> move{used === 1 ? '' : 's'} this week — your next
                  move costs <b className="tabular-nums">{(rate * 100).toFixed(1)}%</b> of
                  {formatFranc(budget)} ≈ <b className="tabular-nums">{formatFranc(nextCost)}</b>.
                </p>
              )}
            </PopoverContent>
          </Popover>
        </div>
      )}

      {team && !team.finalized && rosterFull && (
        <p className="text-[11px] font-semibold text-[hsl(var(--accent))] mb-2" data-testid="finalize-nudge-banner">
          🏆 Your team is complete — hit Finalize below to lock your roster and enter the leaderboard!
        </p>
      )}
      {team && !team.finalized && !rosterFull && picked > 0 && (
        <p className="text-[11px] font-semibold text-[hsl(var(--accent))] mb-2" data-testid="finalize-nudge-banner">
          🏆 {picked} pick{picked === 1 ? '' : 's'} is enough — hit Finalize above to enter the leaderboard now. Open slots fill later, free of any fee.
        </p>
      )}

      <Progress value={progress} className="h-1" />
      <p className="text-[10px] text-muted-foreground mt-1" data-testid="gameweek-deadline" title="Roster moves and weekly points reset every Monday 00:00 UTC">
        Roster moves reset: {formatResetLabel(gw)}
      </p>
    </div>
  );
});

// 2026-09-13 — the reset is Monday 00:00 UTC; render it in the player's own
// timezone ("Sun Sep 13, 8:00 PM" in New York) and fall back to the
// server's UTC label when Intl is unavailable.
export function formatResetLabel(gw) {
  try {
    const d = new Date(gw.deadline);
    if (Number.isNaN(d.getTime())) return gw.deadline_label || '';
    return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return gw.deadline_label || '';
  }
}
